/**
 * Single source of truth for "is this recipe an actual meal?" detection.
 *
 * Used by the fan suggestions (Remplacer ce plat) and the week generator so
 * that a cheesecake can never be proposed as souper, even when the recipe's
 * dishTypes were never classified (legacy imports have dishTypes: []).
 *
 * dishTypes are read through canonicalDishTypes(), never by English substring
 * match: real data arrives tagged in the source site's own language and
 * phrasing — ["Déjeuners et brunchs, Recettes"], ["Collation"], ["Boisson"] —
 * and `dt.includes('breakfast')` says no to every one of them. Titles are
 * matched accent-folded for the same reason.
 *
 * Structural typing on purpose: accepts both RecipeIndexEntry
 * (unifiedRecipeService) and PersonalRecipeIndexEntry (personalRecipeIndex).
 */

import {
  canonicalDishTypes, fold, type CanonicalDishType,
} from './dishTypeVocabulary';

export interface ClassifiableRecipe {
  title: string;
  dishTypes?: string[];
}

// Canonical dish types that disqualify a recipe as a standalone lunch/dinner
// meal. 'breakfast' is conditional via allowBreakfast.
export const NON_MEAL_DISH_TYPES: readonly CanonicalDishType[] = [
  'snack', 'beverage', 'sauce', 'dessert', 'side dish', 'appetizer',
];

// Desserts — patterns consolidated from multiple sources (previously duplicated
// across weekGeneratorService and classifyDishType; mealAlternativesService
// has since been removed). Written accent-free: matched against fold(title).
export const DESSERT_TITLE_RE =
  /\b(gateaux?|cheesecakes?|cakes?|brownies?|cookies?|biscuits?|muffins?|cupcakes?|tartes?|tartelettes?|tarts?|pies?|sorbets?|glace|ice cream|creme brulee|pouding|puddings?|mousse|crumbles?|compotes?|confitures?|fudge|truffles?|macarons?|meringues?|tiramisu|clafoutis|panna cotta|shortbread|scones?|sugar cookie|sucre a la creme|brisures de chocolat|chocolate chip|cremeux au chocolat|barres? (tendres?|granola)|pops? glaces?)\b/;

// Savory dishes whose titles contain a dessert word ("crab cakes", "shepherd's
// pie") — checked before DESSERT_TITLE_RE. Kept in code because regex
// lookbehind is not reliably available on Hermes.
const SAVORY_DESSERT_WORD_RE =
  /\b((crab|fish|potato|rice|salmon|corn|crispy) cakes?|galettes? de|(shepherd'?s?|cottage|pot|chicken|meat|tourtiere) pies?|pie aux?|tartes? (flambee|a l'oignon|aux oignons|aux tomates|au fromage))\b/;

// Beverages and snacks/condiments. Deliberately avoids patterns that appear in
// real dinner titles: no bare "sauce" (Pork Chops With Cherry Sauce), no bare
// "chips" (fish and chips), no bare "jus" (rôti au jus).
export const BEVERAGE_SNACK_TITLE_RE =
  /\b(smoothies?|jus (de|d')|juice|cocktails?|mocktails?|milkshakes?|slushies?|latte|frappe|kombucha|boisson|lemonade|limonade|milk shake|mimosas?|sangrias?|spritz|mojitos?|margaritas?|martinis?|negronis?|bellinis?|daiquiris?|pi[nn]a colada|(aperol|whisky|whiskey|gin|amaretto|pisco) sour|punch|trail mix|popcorn|energy balls?|granola bars?|barres? granola|frozen grapes|rice krispie|cheese straws?|pretzel bites?|deviled eggs?|fruit leather|snack mix|cheese ball|fruit salad|salade de fruits|(pizza |pie )?dough|pate a|vinaigrettes?|dressing|marinade)\b/;

// Condiment-style recipes are only disqualified when the title *starts* with
// the condiment word ("Sauce bolognaise"), not when it merely contains it.
export const CONDIMENT_TITLE_START_RE =
  /^(sauces?|dips?|salsas?|marinades?|vinaigrettes?|pestos?|mayonnaises?|ketchups?)\b/;

// Québec French counts: "déjeuner" is breakfast, not lunch — the same reading
// the import classifier and the Recipes-tab filters already use.
export const BREAKFAST_TITLE_RE =
  /\b(pancakes?|waffles?|omelettes?|porridge|acai|cereal|oatmeal|breakfast|brioche|crepes?|pain dore|gruau|granola|muesli|bircher|overnight oats|bols? smoothie|smoothie bowls?|dejeuners?|petit-dej|brunchs?)\b/;

export function isDessertRecipe(recipe: ClassifiableRecipe): boolean {
  if (canonicalDishTypes(recipe.dishTypes).includes('dessert')) return true;
  const title = fold(recipe.title);
  if (SAVORY_DESSERT_WORD_RE.test(title)) return false;
  return DESSERT_TITLE_RE.test(title);
}

export interface NonMealOptions {
  /** Breakfast slots should not reject breakfast recipes. Default false. */
  allowBreakfast?: boolean;
}

export function isBreakfastRecipe(recipe: ClassifiableRecipe): boolean {
  if (canonicalDishTypes(recipe.dishTypes).includes('breakfast')) return true;
  return BREAKFAST_TITLE_RE.test(fold(recipe.title));
}

/**
 * Is this a morning dish first, or merely a dish whose tags happen to mention
 * breakfast? Scraped dishTypes often arrive as one comma-joined blob
 * ("breakfast, dinner, easy, lunch, main course"), so `isBreakfastRecipe` says
 * yes to a 1h35 bean stew. The generator uses this to serve real breakfast
 * dishes first and fall back to the ambiguous ones only when nothing is left.
 */
export function isPrimarilyBreakfastRecipe(recipe: ClassifiableRecipe): boolean {
  if (BREAKFAST_TITLE_RE.test(fold(recipe.title))) return true;
  const canonical = canonicalDishTypes(recipe.dishTypes);
  if (!canonical.includes('breakfast')) return false;
  // 'entree'/'dinner'/'lunch'/'supper' all canonicalise into these two.
  return !canonical.includes('main course') && !canonical.includes('appetizer');
}

export function isNonMealRecipe(recipe: ClassifiableRecipe, opts: NonMealOptions = {}): boolean {
  const canonical = canonicalDishTypes(recipe.dishTypes);

  if (canonical.length > 0) {
    // The recipe has been classified, so the classification is the answer.
    //
    // This branch used to fall through to the title heuristics below, on the
    // reasoning that a "Cheesecake" mistagged 'main course' should still lose.
    // That trade stopped paying: labels are now canonical values written by a
    // classifier that requires two independent layers to agree and otherwise
    // writes nothing (#609), while the title rules are a bag of substrings that
    // barred 42 real dinners — savory tarts on `tartes?`, dinner salads on
    // `vinaigrette`, a leg of lamb on `jus de`. A wrong label is now a data bug
    // the quality checker and eval:dish-types are built to surface; it should
    // not cost every correctly-labelled recipe its dinner slot.
    //
    // The label-based bars below are unchanged — a 'main course' tag has never
    // rescued a recipe that also carries a non-meal tag, and still does not.
    if (canonical.some((dt) => NON_MEAL_DISH_TYPES.includes(dt))) return true;
    // A blob tagged both breakfast and main course ("Déjeuners et brunchs, Plats
    // principaux") is a meal — the breakfast tag alone must not bar it from souper.
    if (!opts.allowBreakfast && canonical.includes('breakfast') && !canonical.includes('main course')) {
      return true;
    }
    return false;
  }

  // No label at all: fall back to reading the title. Unchanged behaviour — this
  // is the only path these heuristics were ever meant to serve.
  const title = fold(recipe.title);
  if (isDessertRecipe(recipe)) return true;
  if (BEVERAGE_SNACK_TITLE_RE.test(title)) return true;
  if (CONDIMENT_TITLE_START_RE.test(title.trim())) return true;
  if (!opts.allowBreakfast && BREAKFAST_TITLE_RE.test(title)) return true;

  return false;
}
